import { Alert, Tab, Tabs } from "@mui/material";
import { Plus } from "lucide-react";
import * as React from "react";

import { useHasOrgRole } from "@/providers/auth/hooks";
import {
  CustomFieldRecord,
  CustomFieldStatus,
  TargetEntityType,
} from "@/shared/domain/customFields";
import { useCustomFields } from "@/shared/domain/customFields/hooks";
import { Button } from "@/shared/foundation/primitives";
import { RobotoDomainException } from "@/shared/http";

import { SettingsPageLayout } from "../SettingsPageLayout";

import { CreateCustomFieldDialog } from "./CreateCustomFieldDialog";
import styles from "./CustomFields.module.css";
import { CustomFieldsTable } from "./CustomFieldsTable";
import { DeleteCustomFieldDialog } from "./DeleteCustomFieldDialog";
import { EditCustomFieldDialog } from "./EditCustomFieldDialog";
import { ENTITY_TYPE_TABS } from "./entityTypeTabs";

// Show every lifecycle state so in-flight creates/deletes stay visible (the
// hook polls while any field is transitional). Derived from the enum so a new
// status is picked up automatically; we pass them explicitly because the API's
// empty-statuses default omits Deleting, which we need to keep delete in view.
const LIST_STATUSES = Object.values(CustomFieldStatus);

export function CustomFields() {
  const isAdmin = useHasOrgRole("admin");
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editTarget, setEditTarget] = React.useState<CustomFieldRecord | null>(
    null,
  );
  const [deleteTarget, setDeleteTarget] =
    React.useState<CustomFieldRecord | null>(null);
  const [entityType, setEntityType] = React.useState<TargetEntityType>(
    TargetEntityType.Device,
  );

  // No memo needed: TanStack hashes the query key structurally, so a fresh
  // request object with identical contents doesn't trigger an extra fetch.
  const customFieldsQuery = useCustomFields({
    request: { entity_type: entityType, statuses: LIST_STATUSES },
  });

  let readError: string | null = null;
  if (customFieldsQuery.isError) {
    readError =
      customFieldsQuery.error instanceof RobotoDomainException
        ? customFieldsQuery.error.message
        : "Failed to load custom fields. Please try refreshing the page.";
  }

  return (
    <SettingsPageLayout>
      <div className={styles.header}>
        <h2 className={styles.title}>Custom Fields</h2>
        <p className={styles.subtitle}>
          Define typed custom fields for your entities.
        </p>
      </div>

      <div className={styles.toolbar}>
        <Tabs
          value={entityType}
          onChange={(_event, next: TargetEntityType) => {
            setEntityType(next);
          }}
          variant="scrollable"
          scrollButtons="auto"
          className={styles.entityTabs}
        >
          {ENTITY_TYPE_TABS.map(({ entityType: value, label, Icon }) => (
            <Tab
              key={value}
              value={value}
              label={label}
              icon={<Icon size={16} />}
              iconPosition="start"
            />
          ))}
        </Tabs>
        {isAdmin ? (
          <Button
            variant="contained"
            size="small"
            startIcon={<Plus size={16} />}
            onClick={() => setCreateOpen(true)}
            className={styles.newFieldButton}
          >
            New Field
          </Button>
        ) : null}
      </div>

      {readError ? (
        <Alert severity="error" className={styles.errorAlert}>
          {readError}
        </Alert>
      ) : null}

      <CustomFieldsTable
        fields={customFieldsQuery.data?.items ?? []}
        isLoading={customFieldsQuery.isPending}
        onEditField={setEditTarget}
        onDeleteField={setDeleteTarget}
      />

      <CreateCustomFieldDialog
        open={createOpen}
        entityType={entityType}
        onClose={() => setCreateOpen(false)}
      />

      <EditCustomFieldDialog
        field={editTarget}
        onClose={() => setEditTarget(null)}
      />

      <DeleteCustomFieldDialog
        field={deleteTarget}
        onClose={() => setDeleteTarget(null)}
      />
    </SettingsPageLayout>
  );
}
